import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

const YEARN_TICK_INTERVAL = 0.55;
const HEARTBEAT_INTERVAL = 1.25; // point-blank ring cadence while the embrace holds

/**
 * Jealous Embrace (The Jealous Regent's signature) — the anti-kiting
 * pattern. A devotion ring hangs around the Regent; stand OUTSIDE it and
 * you are dragged back in while "yearning" ticks chip you down — the
 * Regent will not be left. Inside the ring you are safe from the pull but
 * dancing point-blank: the Regent's heart beats slow rings outward that
 * must be dodged at hugging distance. Every other fight in the game is won
 * by keeping distance; this one punishes exactly that instinct, and the
 * survivor's whole toolkit (dash through rings, orbit tight) has to work
 * at a range it was never used at. The pull respects dash i-frames (same
 * "cutting through" rule as Riptide Vortex) and is always weaker than
 * player move speed, so leaving is possible — just never free.
 */
export class JealousEmbrace extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Jealous Embrace', telegraphDuration: 1.2, activeDuration: 6.5, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin(boss) {
    // Phase 2+: the Regent wants you even closer.
    this._radius = boss.phase >= 2 ? this.config.embraceRadius * 0.85 : this.config.embraceRadius;
    this._yearnTimer = 0;
    this._heartbeatTimer = HEARTBEAT_INTERVAL * 0.6; // first ring lands early, sets the rhythm
    this._playerOutside = false;
  }

  onActiveTick(dt, boss, player, pool) {
    const dx = boss.x - player.x;
    const dy = boss.y - player.y;
    const dist = Math.hypot(dx, dy);
    this._playerOutside = dist > this._radius;

    if (this._playerOutside) {
      if (!player.isDashing) {
        // The drag: stronger the farther they've strayed, never stronger
        // than their own legs.
        const strain = Math.min(1, (dist - this._radius) / 90);
        const strength = this.config.pullStrength * (0.6 + 0.4 * strain);
        player.x += (dx / dist) * strength * dt;
        player.y += (dy / dist) * strength * dt;
        player.x = Math.min(ARENA.x + ARENA.width - player.radius, Math.max(ARENA.x + player.radius, player.x));
        player.y = Math.min(ARENA.y + ARENA.height - player.radius, Math.max(ARENA.y + player.radius, player.y));
      }
      this._yearnTimer -= dt;
      if (this._yearnTimer <= 0 && !player.isInvincible) {
        player.takeDamage(this.config.yearnDamage);
        this._yearnTimer = YEARN_TICK_INTERVAL;
      }
    } else {
      this._yearnTimer = 0; // ticks restart fresh next time they stray
    }

    // The heartbeat: slow point-blank rings that make the inside a dance,
    // not a refuge.
    this._heartbeatTimer -= dt;
    if (this._heartbeatTimer <= 0) {
      this._heartbeatTimer = HEARTBEAT_INTERVAL - boss.phase * 0.1;
      const count = this.config.ringCount + boss.phase * 2;
      const baseAngle = Math.random() * Math.PI * 2;
      for (let i = 0; i < count; i++) {
        const angle = baseAngle + (i / count) * Math.PI * 2;
        pool.fire({
          x: boss.x,
          y: boss.y,
          vx: Math.cos(angle) * this.config.ringSpeed,
          vy: Math.sin(angle) * this.config.ringSpeed,
          radius: 2.5,
          damage: this.config.ringDamage,
          color: '#ff87b3',
          lifetime: 3.5,
        });
      }
    }
  }

  getVisual(boss) {
    if (this.state !== PatternState.TELEGRAPH && this.state !== PatternState.ACTIVE) return null;
    return {
      kind: 'embrace',
      x: boss.x,
      y: boss.y,
      radius: this._radius,
      t: this.state === PatternState.TELEGRAPH ? 1 - this._timer / this.telegraphDuration : 1,
      active: this.state === PatternState.ACTIVE,
      playerOutside: this.state === PatternState.ACTIVE && this._playerOutside,
    };
  }
}
